export type LegalDoc = {
    slug: string
    title: string
    updated: string
    intro: string
    sections: { heading: string, body: string[] }[]
}

export const legalDocs: LegalDoc[] = [
    {
        slug: 'privacy',
        title: 'Privacy Policy',
        updated: 'Last updated: 1 January 2026',
        intro: 'Noma Studio collects as little as it can get away with, keeps it only as long as it is useful, and never sells it. This page explains exactly what that means.',
        sections: [
            {
                heading: 'What we collect',
                body: [
                    'When you send an enquiry we store the name, company, email address, budget, timeline and project description you typed into the form. When you join the letter we store your email address and the date you joined.',
                    'We also collect privacy-friendly, cookieless visitor statistics: which pages were viewed, roughly where in the world the visit came from, and what referred it. None of it identifies you personally and none of it follows you to other sites.'
                ]
            },
            {
                heading: 'Why we collect it',
                body: [
                    'Enquiry details exist so the founder can reply to you and scope the work. Newsletter addresses exist so we can send the monthly letter. Visitor statistics exist so we know which work is worth writing about.',
                    'We do not use any of it for advertising, profiling or automated decision-making.'
                ]
            },
            {
                heading: 'How long we keep it',
                body: [
                    'Enquiries are kept for two years, so we have the history if you come back to us. Newsletter addresses are kept until you unsubscribe, which you can do from the bottom of any letter.',
                    'Ask us to delete anything at any time and it goes within thirty days.'
                ]
            },
            {
                heading: 'Who else sees it',
                body: [
                    'Our hosting and email providers process this data on our behalf under contract. Nobody else. We do not sell, rent or trade personal data, and we never will.'
                ]
            },
            {
                heading: 'Your rights',
                body: [
                    'You can ask for a copy of everything we hold about you, ask us to correct it, or ask us to erase it. Email the studio and we will handle it without making you fill in a form.'
                ]
            }
        ]
    },
    {
        slug: 'terms',
        title: 'Terms & Conditions',
        updated: 'Last updated: 1 January 2026',
        intro: 'These terms cover how we work together. They are deliberately short, because a contract nobody reads protects nobody.',
        sections: [
            {
                heading: 'Scope and price',
                body: [
                    'Every project is quoted at a fixed price against a written scope agreed before work starts. The figure we lock on the first call holds unless the scope itself changes, and any change is agreed in writing before it is carried out.',
                    'Monthly membership covers one active request at a time. It can be paused or cancelled at the end of any billing month.'
                ]
            },
            {
                heading: 'Payment',
                body: [
                    'Projects are invoiced fifty percent on acceptance and fifty percent on handover. Membership is billed monthly in advance. Invoices are payable within fourteen days.'
                ]
            },
            {
                heading: 'Revisions and timing',
                body: [
                    'Project work includes three rounds of revisions within the agreed scope. Timelines assume feedback within three working days; longer gaps move the delivery date by the same amount.'
                ]
            },
            {
                heading: 'Ownership',
                body: [
                    'On final payment, every deliverable becomes yours outright: source files, fonts we licensed for you, and the right to use, change and extend the work however you like.',
                    'We keep the right to show the finished work in our portfolio, unless you ask us in writing not to.'
                ]
            },
            {
                heading: 'Liability',
                body: [
                    'We stand behind our work, but our total liability under any engagement is limited to the fees paid for it. We are not liable for indirect or consequential loss.'
                ]
            }
        ]
    },
    {
        slug: 'cookies',
        title: 'Cookies',
        updated: 'Last updated: 1 January 2026',
        intro: 'This site sets no tracking cookies and shows no cookie banner, because there is nothing to consent to.',
        sections: [
            {
                heading: 'What we use',
                body: [
                    'Visitor statistics are collected without cookies and without any identifier that persists between visits. Nothing is stored on your device for analytics purposes.',
                    'Your browser may keep a small amount of local data to remember interface preferences, such as whether you have already seen the opening animation. That never leaves your device.'
                ]
            },
            {
                heading: 'Third parties',
                body: [
                    'We embed no advertising networks, no social pixels and no session recorders. Fonts and media are served from our own infrastructure.'
                ]
            },
            {
                heading: 'Changes',
                body: [
                    'If we ever add something that does require consent, we will ask for it properly before it runs, and this page will say so first.'
                ]
            }
        ]
    }
]

export const getLegalDoc = (slug: string): LegalDoc | undefined => {
    return legalDocs.find((doc) => doc.slug === slug)
}
